import React from "react";
import {Box, Tooltip, Typography} from "@mui/material";
import {fmtCompact, fmtSignedPct, isNilValue} from "./format";
import {TABULAR, MONO, WARNING_TEXT} from "./tokens";

const NO_ADS_SHOWN = 2;
const NO_ADS_LABEL = "no ads > 20%";
const NO_NO_ADS = "no tag without ads";
const DELTA_ALARM = -25; // sent today ≤ 25 % below yesterday at the same hour → amber

const hourText = (h) => (isNilValue(h) ? null : `${String(Math.floor(Number(h))).padStart(2, "0")}:00`);
const tagsOf = (today) => (today && Array.isArray(today.tags_no_ads) ? today.tags_no_ads.filter(Boolean) : []);
const deltaOf = (today) => (today && !isNilValue(today.delta_pct) ? Number(today.delta_pct) : null);

/** The pieces of the line; `todayText` joins them, the component colours them. */
const parts = (today) => {
    const hh = hourText(today.as_of_hour);
    const sent = fmtCompact(today.fs_today);
    const delta = hh ? deltaOf(today) : null; // no hour → no like-for-like comparison
    const tags = tagsOf(today);
    return {
        hh,
        head: hh ? `today to ${hh} UTC · ${sent} sent` : `today · ${sent} sent so far`,
        delta,
        deltaText: delta === null ? "" : ` (${fmtSignedPct(delta)} vs yesterday same hour)`,
        tags,
        shown: tags.slice(0, NO_ADS_SHOWN),
        rest: Math.max(0, tags.length - NO_ADS_SHOWN),
    };
};

/** Plain-text version: `today to 20:00 UTC · 953k sent (+4% vs yesterday same hour) · no ads > 20%: gp13, gp14 (+1)`. */
export const todayText = (today) => {
    if (!today) return "";
    const p = parts(today);
    const noAds = p.tags.length ? ` · ${NO_ADS_LABEL}: ${p.shown.join(", ")}${p.rest ? ` (+${p.rest})` : ""}` : ` · ${NO_NO_ADS}`;
    return `${p.head}${p.deltaText}${noAds}`;
};

const Dot = ({color, size = 6}) => (
    <Box component="span" aria-hidden="true" sx={{display: "inline-block", width: size, height: size, borderRadius: "50%", bgcolor: color, mr: 0.75, verticalAlign: "middle", position: "relative", top: -1}}/>
);

/**
 * Intraday line (Tiger only): searches sent so far today vs yesterday at the same hour, and the tags serving no ads.
 * Grey while nothing is wrong; amber when a tag has no ads or sent fell ≥ 25 %. Renders nothing without `today`.
 */
const TodayLine = ({today, phone = false, sx}) => {
    if (!today) return null;
    const p = parts(today);
    // Before 06 UTC the day is too young to judge: a sent delta is information, never an alarm ("early").
    const hourNum = Number.isFinite(Number(today.as_of_hour)) ? Number(today.as_of_hour) : null;
    const early = hourNum !== null && hourNum < 6;
    const alarmDelta = !early && p.delta !== null && p.delta <= DELTA_ALARM;
    const alarmTags = p.tags.length > 0;
    const warn = alarmTags || alarmDelta;
    const quiet = !warn && (p.delta === null || Math.abs(p.delta) <= 10);
    const color = warn ? WARNING_TEXT : quiet ? "text.secondary" : "text.primary";
    const text = todayText(today);
    const tip = `Tiger only — revenue is not known until partners report. ${p.hh ? `Sent today at ${p.hh} vs yesterday at the same hour.` : "Sent so far today."}`;

    return (
        <Tooltip title={`${text} — ${tip}`} placement="top" arrow enterDelay={400}>
            <Typography component="div" noWrap={!phone} sx={{
                fontSize: 12, lineHeight: 1.4, color, ...TABULAR, minWidth: 0,
                ...(phone ? {display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden"} : {}),
                ...sx,
            }}>
                <Dot color={warn ? (t) => t.palette.warning.main : "grey.400"}/>
                {early ? "early · " : ""}{p.head}
                {p.deltaText && <Box component="span" sx={{fontWeight: alarmDelta ? 600 : 400}}>{p.deltaText}</Box>}
                {alarmTags ? (
                    <Box component="span" title={`${NO_ADS_LABEL}: ${p.tags.join(", ")}`} sx={{fontWeight: 600}}>
                        {" · "}{NO_ADS_LABEL}:{" "}
                        <Box component="span" sx={{fontFamily: MONO, fontSize: 11.5}}>{p.shown.join(", ")}</Box>
                        {p.rest > 0 && ` (+${p.rest})`}
                    </Box>
                ) : (
                    <Box component="span" sx={{color: "text.disabled"}}> · {NO_NO_ADS}</Box>
                )}
            </Typography>
        </Tooltip>
    );
};

export default TodayLine;
